/**
 * File Processing Helpers
 * Shared functions for file import processing popup and status polling
 */

import {fileUploadService} from './file-upload-service';

// Extend Window interface for BookStack event system
declare global {
    interface Window {
        $events: {
            emit(event: string, data?: any): void;
            listen(event: string, callback: (data?: any) => void): void;
        };
    }
}

/**
 * Show processing popup with status polling
 * @param jobId - Job ID to track
 * @param entityType - 'book' | 'chapter'
 * @param options - Additional options
 */
export function showProcessingPopup(
    jobId: string,
    entityType: 'book' | 'chapter',
    options: ProcessingOptions = {}
): void {
    const entityTypeLabel = entityType === 'book' ? 'Book' : 'Chapter';
    const actionLabel = entityType === 'book' ? 'Importing' : 'Replacing';

    // Create popup using project's popup structure
    const popup = document.createElement('div');
    popup.className = 'popup-background';
    popup.id = 'file-processing-popup';
    popup.style.display = 'flex';

    popup.innerHTML = `
        <div class="popup-body very-small" tabindex="-1">
            <div class="popup-header primary-background">
                <div class="popup-title">${actionLabel} ${entityTypeLabel}</div>
                <button class="popup-header-close" type="button">×</button>
            </div>

            <div class="popup-content processing-content">
                <div class="processing-spinner"></div>
                <div class="processing-message">Uploading file...</div>
                <div class="processing-status">Status: Initializing...</div>
                <div class="processing-progress">
                    <div class="progress-bar">
                        <div class="progress-fill"></div>
                    </div>
                    <div class="progress-text">0%</div>
                </div>
            </div>

            <div class="popup-footer">
                <button class="button" type="button">Cancel</button>
            </div>
        </div>
    `;

    document.body.appendChild(popup);

    // Get elements
    const closeBtn = popup.querySelector('.popup-header-close') as HTMLElement;
    const cancelBtn = popup.querySelector('.button') as HTMLElement;
    const messageEl = popup.querySelector('.processing-message') as HTMLElement;
    const statusEl = popup.querySelector('.processing-status') as HTMLElement;
    const progressFillEl = popup.querySelector('.progress-fill') as HTMLElement;
    const progressTextEl = popup.querySelector('.progress-text') as HTMLElement;

    // State
    let isCancelled = false;
    let pollInterval: number | null = null;

    // Cleanup function
    const cleanup = () => {
        isCancelled = true;
        if (pollInterval) {
            clearInterval(pollInterval);
        }
        popup.remove();
    };

    // Event handlers
    closeBtn?.addEventListener('click', () => {
        if (confirm('Are you sure you want to cancel? The file processing will continue in the background.')) {
            cleanup();
        }
    });

    cancelBtn?.addEventListener('click', async () => {
        if (confirm('Are you sure you want to cancel the processing?')) {
            try {
                await fileUploadService.cancelJob(jobId);
                window.$events.emit('warning', 'Processing cancelled');
            } catch (error) {
                console.error('Failed to cancel job:', error);
            }
            cleanup();
        }
    });

    // Close on popup background click
    popup.addEventListener('click', (e) => {
        if (e.target === popup) {
            if (confirm('Are you sure you want to close this window? The processing will continue in the background.')) {
                cleanup();
            }
        }
    });

    // Start polling
    const pollJobStatus = async () => {
        try {
            const status = await fileUploadService.checkStatus(jobId);

            if (isCancelled) return;

            // Update UI
            statusEl.textContent = `Status: ${formatStatus(status.status)}`;
            progressFillEl.style.width = `${status.progress}%`;
            progressTextEl.textContent = `${status.progress}%`;

            if (status.message) {
                messageEl.textContent = status.message;
            }

            // Handle completion
            if (status.status === 'completed') {
                cleanup();
                showSuccessMessage(entityType, options.successMessage);

                // Auto refresh after delay to show new content
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
                return;
            }

            // Handle failure
            if (status.status === 'failed') {
                cleanup();
                const errorMsg = status.error || 'Processing failed. Please try again.';
                console.log(errorMsg);

                showErrorMessage("Có lỗi xảy ra, pipline bị lỗi");
                return;
            }

        } catch (error) {
            console.error('Status polling error:', error);
            if (!isCancelled) {
                messageEl.textContent = 'Error checking status...';
                statusEl.textContent = 'Status: Error';

                // Show error notification
                showErrorMessage('Failed to check processing status. Please refresh the page.');
            }
        }
    };

    // Start polling immediately, then every 2 seconds
    pollJobStatus();
    pollInterval = window.setInterval(pollJobStatus, 2000);

    // Auto-cancel after 10 minutes (600 seconds)
    setTimeout(() => {
        if (!isCancelled) {
            cleanup();
            showErrorMessage('Processing timeout. Please check back later.');
        }
    }, 600000);
}

/**
 * Show processing modal (alternative to popup)
 * @param jobId - Job ID to track
 * @param entityType - 'book' | 'chapter'
 */
export function showProcessingModal(
    jobId: string,
    entityType: 'book' | 'chapter'
): HTMLElement {
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.id = 'file-processing-modal';

    const entityTypeLabel = entityType === 'book' ? 'Book' : 'Chapter';
    const actionLabel = entityType === 'book' ? 'Importing' : 'Replacing';

    modal.innerHTML = `
        <div class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h3>📁 ${actionLabel} ${entityTypeLabel}</h3>
                </div>
                <div class="modal-body">
                    <div class="processing-content">
                        <div class="processing-spinner"></div>
                        <div class="processing-message">Processing file...</div>
                        <div class="processing-status">Status: Checking...</div>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    // Start polling status
    const statusEl = modal.querySelector('.processing-status') as HTMLElement;
    const messageEl = modal.querySelector('.processing-message') as HTMLElement;

    const pollInterval = setInterval(async () => {
        try {
            const status = await fileUploadService.checkStatus(jobId);

            statusEl.textContent = `Status: ${formatStatus(status.status)}`;

            if (status.message) {
                messageEl.textContent = status.message;
            }

            if (status.status === 'completed') {
                clearInterval(pollInterval);
                modal.remove();
                showSuccessMessage(entityType);
                window.location.reload();
            } else if (status.status === 'failed') {
                clearInterval(pollInterval);
                modal.remove();
                showErrorMessage(status.error || 'Processing failed');
            }

        } catch (error) {
            console.error('Status polling error:', error);
        }
    }, 2000);

    return modal;
}

/**
 * Show success message using BookStack notification system
 */
function showSuccessMessage(entityType: 'book' | 'chapter', customMessage?: string): void {
    const action = entityType === 'book' ? 'imported' : 'replaced';
    const message = customMessage || `File ${action} successfully! Refreshing...`;

    window.$events.emit('success', message);
}

/**
 * Show error message using BookStack notification system
 */
function showErrorMessage(message: string): void {
    window.$events.emit('error', message);
}

/**
 * Format status for display
 */
function formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
        'pending': 'Waiting...',
        'processing': 'Processing...',
        'completed': 'Completed',
        'failed': 'Failed'
    };

    return statusMap[status] || status;
}

/**
 * Check if processing popup is currently shown
 */
export function isProcessingPopupShown(): boolean {
    return !!document.getElementById('file-processing-popup');
}

/**
 * Hide any existing processing popup
 */
export function hideProcessingPopup(): void {
    const popup = document.getElementById('file-processing-popup');
    if (popup) {
        popup.remove();
    }
}

/**
 * Create and show upload progress modal
 */
export function showUploadProgressModal(fileName: string): Promise<void> {
    return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'modal';
        modal.id = 'upload-progress-modal';

        modal.innerHTML = `
            <div class="modal-overlay">
                <div class="modal-content">
                    <div class="modal-header">
                        <h3>📤 Uploading File</h3>
                    </div>
                    <div class="modal-body">
                        <div class="upload-progress">
                            <div class="upload-filename">${fileName}</div>
                            <div class="progress-bar">
                                <div class="progress-fill" style="width: 0%"></div>
                            </div>
                            <div class="progress-text">0%</div>
                        </div>
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Store reference for progress updates
        (window as any).uploadProgressModal = {
            modal,
            updateProgress: (progress: number) => {
                const progressFill = modal.querySelector('.progress-fill') as HTMLElement;
                const progressText = modal.querySelector('.progress-text') as HTMLElement;
                if (progressFill) progressFill.style.width = `${progress}%`;
                if (progressText) progressText.textContent = `${progress}%`;
            },
            close: () => {
                modal.remove();
                delete (window as any).uploadProgressModal;
                resolve();
            }
        };
    });
}

// Type definitions
interface ProcessingOptions {
    successMessage?: string;
    timeout?: number;
}

// Export for global access
(window as any).showProcessingPopup = showProcessingPopup;
(window as any).showProcessingModal = showProcessingModal;
(window as any).hideProcessingPopup = hideProcessingPopup;